import { AxiosRequestHeaders, AxiosResponse, Method } from 'axios';
import PersonaApiClient, { Endpoints } from 'services/PersonaApiClient';
import { HttpOptions } from 'services/CwApiClient';
import ApiServiceError from 'support/ApiServiceError';
import { ErrorType } from 'types/analytics';
import { EpgChannel, Media, Video } from 'types/api/media';
import { getVideoProgressPercent, isInCredits } from 'support/contentUtils';
import { AccessTokenResponse } from 'types/loginRadius';
import LoginRadiusService from './LoginRadiusService';
import { ErrorCode } from './analytics/reportingServicePage';
import { getShowByShowSlug, getVideo, getVideos } from './cwService';
import { VideoLaneItem } from './cwData';
import { VideoHistory } from './history/HistoryService';

export type ContinueWatchingItem = {
  content_group_id: string;
  content_type: string;
  progress_percent: number;
  content_id: string;
  last_updated?: number;
};

export type BatchSyncItem = ContinueWatchingItem & {
  last_updated: number;
};

export type WatchlistItem = {
  content_group_id: string;
  content_title: string;
  content_type: 'movie' | 'series';
  content_release: number;
  last_updated: number;
};

export type FavoriteChannelItem = {
  channel_id: string;
  content_title: string;
  content_type: string;
  last_updated: number;
};

export type SottResponse = {
  Sott: string;
  Expiry: string;
};

type AuthOptions = {
  useBearerToken?: boolean;
  useApiKey?: boolean;
};

type PersonaServiceParams = {
  apiKey: string;
};

const client = new PersonaApiClient();

export default class PersonaService {
  static readonly serviceName = 'PersonaService';

  private apiKey?: string;
  private loginRadiusService!: LoginRadiusService;

  constructor(params?: PersonaServiceParams) {
    this.apiKey = params?.apiKey;
  }

  init(loginRadiusService: LoginRadiusService) {
    this.loginRadiusService = loginRadiusService;
  }

  private async sendRequest<TResult, TResponse>(
    method: Method,
    endpoint: string,
    options?: HttpOptions & AuthOptions,
    parser?: (response: TResponse) => TResult,
  ) {
    const response = await client.request<TResponse>(method, endpoint, {
      ...options,
      headers: {
        ...(await this.getAuthHeaders(
          options?.useBearerToken ?? true,
          options?.useApiKey ?? false,
        )),
        ...options?.headers,
      } as AxiosRequestHeaders,
    });
    return this.handleResponse<TResult, TResponse>(response, parser);
  }

  private handleResponse<TResult, TResponse>(
    response: AxiosResponse,
    parser?: (response: TResponse) => TResult,
  ): TResult {
    let result: TResult;
    if (parser) {
      try {
        result = parser(response.data);
      } catch (e) {
        throw new ApiServiceError(ErrorType.MISSING_CONTENT);
      }
    } else {
      result = response.data as TResult;
    }

    return result;
  }

  private async getAuthHeaders(
    useBearerToken: boolean,
    useApiKey: boolean,
  ): Promise<AxiosRequestHeaders> {
    const headers: Record<string, string> = {};

    if (useBearerToken) {
      const jwt = await this.loginRadiusService.getSsoJwt();
      headers.Authorization = `Bearer ${jwt}`;
    }

    if (useApiKey && this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    }

    return headers as unknown as AxiosRequestHeaders;
  }

  private buildQueryString(ids: string[], paramName: string): string {
    return ids.map(id => `${paramName}=${encodeURIComponent(id)}`).join('&');
  }

  private async getContinueWatchingItems(
    limit?: number,
  ): Promise<ContinueWatchingItem[]> {
    return this.sendRequest<ContinueWatchingItem[], ContinueWatchingItem[]>(
      'GET',
      Endpoints.CONTINUE_WATCHING,
      limit ? { params: { limit } } : {},
    );
  }

  // Batch Load to sync with device's local items
  async batchLoadContinueWatchingItems(
    history: Array<[Video, VideoHistory]>,
    lastWatchedGuids: string[],
  ): Promise<void> {

    const continueWatchingItems: BatchSyncItem[] = history.map(
      ([video, historyItem]) => {
        const progress = historyItem.isComplete
          ? 1.0
          : getVideoProgressPercent(historyItem.progress, video);

        return {
          content_group_id: video.showSlug,
          content_type: video.seriesType,
          content_id: video.guid,
          last_updated: historyItem.lastModified,
          progress_percent: progress,
        };
      },
    );

    const nextVideos = history
      .filter(([video, historyItem]) => {
        return (
          lastWatchedGuids.includes(video.guid) &&
          historyItem.isComplete &&
          !!historyItem.nextEpisode
        );
      })
      .map(([video, historyItem]) => {
        return {
          content_group_id: video.showSlug,
          content_type: video.seriesType,
          content_id: historyItem.nextEpisode!.guid,
          last_updated: historyItem.lastModified,
          progress_percent: 0,
        };
      });

    // De-dupe edge cases where next videos have been watched before
    const latestItems = new Map<string, BatchSyncItem>();
    [...continueWatchingItems, ...nextVideos].forEach(item => {
      const existing = latestItems.get(item.content_id);
      if (!existing || item.last_updated >= existing.last_updated) {
        latestItems.set(item.content_id, item);
      }
    });

    return this.sendRequest<void, void>('PUT', Endpoints.CONTINUE_WATCHING, {
      data: Array.from(latestItems.values()),
    });
  }

  async removeContinueWatchingItems(contentIds: string[]): Promise<void> {
    const queryString = this.buildQueryString(contentIds, 'ids');
    const url = `${Endpoints.CONTINUE_WATCHING}?${queryString}`;

    return this.sendRequest<void, void>('DELETE', url);
  }

  async upsertContinueWatchingItem(item: ContinueWatchingItem): Promise<void> {
    return this.sendRequest<void, void>(
      'PUT',
      `${Endpoints.CONTINUE_WATCHING_CONTENT}/${item.content_id}`,
      {
        data: item,
      },
    );
  }

  async updateContentProgress(
    contentId: string,
    progressPercent: number,
  ): Promise<void> {
    return this.sendRequest<void, void>(
      'PATCH',
      `${Endpoints.CONTINUE_WATCHING_CONTENT}/${contentId}`,
      {
        data: { progress_percent: progressPercent },
      },
    );
  }

  async getContentGroup(
    contentGroupId: string,
  ): Promise<Array<Video & { progress: number; last_updated: number }>> {
    const items = await this.sendRequest<
      ContinueWatchingItem[],
      ContinueWatchingItem[]
    >('GET', `${Endpoints.CONTINUE_WATCHING_CONTENT_GROUP}/${contentGroupId}`);

    const continueWatchingMap: Record<string, ContinueWatchingItem> = {};
    const guids: string[] = [];

    items.forEach(item => {
      continueWatchingMap[item.content_id] = item;
      guids.push(item.content_id);
    });
    const videos = await getVideos(guids);

    return videos.map(video => {
      const percent = continueWatchingMap[video.guid]!.progress_percent;
      const progress = (percent ?? 0) * Number(video.durationSecs ?? 0);
      const last_updated = continueWatchingMap[video.guid]!.last_updated!;
      return { ...video, progress, last_updated };
    });
  }

  async getWatchlistItems(params: {
    contentType?: 'series' | 'movie';
    limit?: number;
  }): Promise<WatchlistItem[]> {
    return this.sendRequest<WatchlistItem[], WatchlistItem[]>(
      'GET',
      Endpoints.WATCHLIST_SHOWS,
      {
        ...(params && { params }),
      },
    );
  }

  async removeFromWatchlist(ids: string[]): Promise<void> {
    const queryString = this.buildQueryString(
      ids.map(id => id ?? ''),
      'groups',
    );
    const url = `${Endpoints.WATCHLIST_SHOWS}?${queryString}`;

    return this.sendRequest<void, void>('DELETE', url);
  }

  async getWatchlistItem(
    contentGroupId: string,
  ): Promise<WatchlistItem | undefined> {
    let watchlistItem: WatchlistItem | undefined;
    try {
      watchlistItem = await this.sendRequest<WatchlistItem, WatchlistItem>(
        'GET',
        `${Endpoints.WATCHLIST_SHOWS_CONTENT_GROUP}/${contentGroupId}`,
      );
    } catch (e: any) {
      if (e.type !== 'Not Found') {
        // as long as not 'no watchlist item' error
        reportError({ error: e, userFacing: false, code: ErrorCode.c43 });
      }
    }

    return watchlistItem;
  }

  async addToWatchlist(item: Media): Promise<void> {
    const contentGroupId = item.id;
    return this.sendRequest<void, void>(
      'PUT',
      `${Endpoints.WATCHLIST_SHOWS_CONTENT_GROUP}/${contentGroupId}`,
      {
        data: {
          content_title: item.title,
          content_type: item.type,
          content_release: item.releaseYear,
        },
      },
    );
  }

  async getFavoriteChannelItems(
    limit?: number,
  ): Promise<FavoriteChannelItem[]> {
    return this.sendRequest<FavoriteChannelItem[], FavoriteChannelItem[]>(
      'GET',
      Endpoints.FAVORITE_CHANNELS,
      limit ? { params: { limit } } : {},
    );
  }

  async removeFromFavoriteChannels(channelIds: string[]): Promise<void> {
    const queryString = this.buildQueryString(channelIds, 'ids');
    const url = `${Endpoints.FAVORITE_CHANNELS}?${queryString}`;

    return this.sendRequest<void, void>('DELETE', url);
  }

  async getFavoriteChannelItem(
    channelId: string,
  ): Promise<FavoriteChannelItem> {
    return this.sendRequest<FavoriteChannelItem, FavoriteChannelItem>(
      'GET',
      `${Endpoints.FAVORITE_CHANNELS_CHANNEL}/${channelId}`,
    );
  }

  async addToFavoriteChannels(item: EpgChannel): Promise<void> {
    return this.sendRequest<void, void>(
      'PUT',
      `${Endpoints.FAVORITE_CHANNELS_CHANNEL}/${item.id}`,
      {
        data: { content_title: item.title },
      },
    );
  }

  async getLoginSott(): Promise<SottResponse> {
    return this.sendRequest<SottResponse, SottResponse>(
      'GET',
      Endpoints.LOGIN_SOTT,
      { useApiKey: true, useBearerToken: false },
    );
  }

  async refreshAccessToken(refreshToken: string): Promise<AccessTokenResponse> {
    const queryString = this.buildQueryString([refreshToken], 'refreshToken');
    return this.sendRequest(
      'GET',
      `${Endpoints.ACCESS_TOKEN_REFRESH}?${queryString}`,
      {
        useApiKey: true,
        useBearerToken: false,
      },
    );
  }

  async fetchAccountBookmarkItems(limit?: number) {
    const items = await this.getContinueWatchingItems(limit);
    if (!items || items.length === 0) return [];

    const guids = items.map(item => item.content_id);
    const videos = (await getVideos(guids)).filter(video => !!video.title);

    // Add progress and show slug to Video
    const videosWithProgress = videos.map(video => {
      const item = items.find(item => item.content_id === video.guid);
      return {
        ...video,
        showSlug: (item!.content_group_id || video.showSlug) ?? '',
        progress:
          (item!.progress_percent ?? 0) * Number(video.durationSecs ?? 0),
      };
    });

    const promises = videosWithProgress.map(video =>
      this.buildAccountBookmarkLane(video),
    );

    const continueLaneItems: VideoLaneItem[] = [];
    (await Promise.allSettled(promises)).forEach(result => {
      if (result.status === 'fulfilled' && result.value?.length)
        continueLaneItems.push(...result.value);
    });

    return continueLaneItems;
  }

  private async buildAccountBookmarkLane(
    video: Video,
  ): Promise<VideoLaneItem[]> {
    const continueLaneItems: VideoLaneItem[] = [];

    if (!video.title) {
      // Content doesn't exist, fetch show from CMS, first episode with no progress.
      try {
        const show = await getShowByShowSlug(video.showSlug);
        if (show && show.firstEpisode) {
          const firstEpisodeVideo = await getVideo(show.firstEpisode.guid);
          if (firstEpisodeVideo) {
            continueLaneItems.push({
              ...firstEpisodeVideo,
              progress: 0,
            } as VideoLaneItem);
          }
        }
      } catch (error) {
        console.error(error);
      }
    } else {
      const inCredits = isInCredits(video, video.progress ?? 0);

      if (inCredits) {
        // If at credits, add next episode if exists
        if (video.nextEpisode) {
          try {
            const nextEpisode = await getVideo(video.nextEpisode.guid);
            if (nextEpisode) {
              // Move it to the start of the list
              continueLaneItems.unshift({
                ...nextEpisode,
                progress: 0,
              } as VideoLaneItem);
              // Update continue watching
              this.upsertContinueWatchingItem({
                content_group_id: nextEpisode.showSlug,
                content_type: nextEpisode.seriesType,
                progress_percent: 0,
                content_id: nextEpisode.guid,
              });
            }
          } catch (error) {
            console.error(error);
          }
        }
      } else {
        // Add content as is
        continueLaneItems.push({
          ...video,
        } as VideoLaneItem);
      }
    }

    continueLaneItems.forEach(item => (item.isContinueItem = true));

    return continueLaneItems;
  }
}
